import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";
import { Button, Icon, Title } from "rbx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimesCircle, faUserCheck } from "@fortawesome/free-solid-svg-icons";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { useModal } from "../../../../context";
import Confirmation from "../../../../components/Confirmation";
import { DELETE_ONE_EVALUATION } from "../../../Reviews/graphql/reviews.mutations";
import { GET_REVIEWS, GET_REVIEWS_WITH_ONLY_EVALUATIONS } from "../../../Reviews/graphql/reviews.queries";
import formatDate from "../../../../utils/formatDate";
import capitalizeEveryWord from "../../../../utils/capitalizeEveryWord";

const ReviewSummaryItem = ({ evaluation, handleClick, isTemplate, refetchMyReviews }) => {
  const { setModalOpen } = useModal();

  const [deleteOneEvaluation] = useMutation(DELETE_ONE_EVALUATION, {
    refetchQueries: [
      {
        query: GET_REVIEWS,
        variables: {
          where: {
            evaluations: {
              some: {
                authorEmployeeNumber: {
                  contains: evaluation?.authorEmployeeNumber,
                },
              },
            },
          },
          orderBy: [
            {
              name: "asc",
            },
          ],
          sectionsOrderBy: [{ order: "asc" }],
          questionsOrderBy: [{ order: "asc" }],
        },
        fetchPolicy: "network-only",
        awaitRefetchQueries: true,
      },
      {
        query: GET_REVIEWS_WITH_ONLY_EVALUATIONS,
        variables: {
          where: {
            evaluations: {
              some: {
                authorEmployeeNumber: {
                  contains: evaluation?.authorEmployeeNumber,
                },
              },
            },
          },
        },
        fetchPolicy: "network-only",
        awaitRefetchQueries: true,
      },
    ],
  });

  const tooltipMsg =
    evaluation?.status === "COMPLETED"
      ? "Evaluation is completed"
      : "Evaluation has not been started";

  let writer = "Employee being reviewed";
  if (evaluation?.formAuthor?.length) {
    writer = capitalizeEveryWord(evaluation?.formAuthor);
  } else if (evaluation?.author?.length) {
    writer = capitalizeEveryWord(evaluation?.author);
  }

  const manager = evaluation?.manager?.length
    ? capitalizeEveryWord(evaluation?.manager)
    : "Employees manager";

  const dueDate = evaluation?.dateToCompleteBy?.length
    ? formatDate(evaluation?.dateToCompleteBy)
    : "N/A";

  const handleDeleteEvaluation = async () => {
    const performDelete = async () => {
      try {
        await deleteOneEvaluation({
          variables: {
            evaluationId: evaluation?.id,
          },
        });
        toast.success("Evaluation Deleted Successfully");
        if (refetchMyReviews) {
          await refetchMyReviews();
        }
        // Force a page reload to ensure all data is refreshed
        window.location.reload();
      } catch (error) {
        toast.error(error.message);
      } finally {
        setModalOpen(false, "");
      }
    };

    setModalOpen(
      true,
      <Confirmation
        message="Are you sure you want to delete this evaluation? This is a one-time action and cannot be reversed."
        onCancel={() => setModalOpen(false, "")}
        onConfirm={performDelete}
      />
    );
  };

  return (
    <div className="summary-item" key={evaluation?.id}>
      <div className="summary-row">
        <Tooltip arrow placement="right" title={!isTemplate && tooltipMsg}>
          <div className="summary-item-title">
            {evaluation?.status === "INCOMPLETE" && (
              <Icon className="not-started-icon">
                <FontAwesomeIcon icon={faTimesCircle} />
              </Icon>
            )}

            {evaluation?.status === "COMPLETED" && (
              <Icon className="completed-icon">
                <FontAwesomeIcon icon={faUserCheck} />
              </Icon>
            )}

            <p className="bold-text">{evaluation?.title}</p>
          </div>
        </Tooltip>
        <div className="summary-buttons">
          {evaluation?.status === "INCOMPLETE" && !isTemplate && (
            <Button
              id="danger-btn"
              type="button"
              onClick={handleDeleteEvaluation}
            >
              <Title size={6} textColor="white">
                Delete
              </Title>
            </Button>
          )}
          <Button
            className="secondary-btn"
            type="button"
            onClick={() => handleClick(evaluation?.id)}
          >
            <Title size={6} textColor="white">
              View
            </Title>
          </Button>
        </div>
      </div>
      <p>Writer: {writer}</p>
      <p>Manager: {manager}</p>
      <p>Due Date: {dueDate}</p>
    </div>
  );
};

ReviewSummaryItem.propTypes = {
  evaluation: PropTypes.object.isRequired,
  handleClick: PropTypes.func.isRequired,
  isTemplate: PropTypes.bool,
  refetchMyReviews: PropTypes.func,
};

ReviewSummaryItem.defaultProps = {
  isTemplate: false,
  refetchMyReviews: null,
};

export default ReviewSummaryItem;
